import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useParams } from 'quartz-routing/react-router';
import { GET_COLLECTION_BY_SLUG_QUERY as PUBLIC_GET_COLLECTION_BY_SLUG_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/CollectionsRpc-external.dfc.new';
import { GET_COLLECTION_BY_SLUG_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/discovery/studio/rpc/CollectionsRpc.dfc.new';
import { SEARCH_QUERY as PUBLIC_SEARCH_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationPublicRpc.dfc.new';
import { SEARCH_QUERY } from '../../../__generated__/chirp/com/hubspot/marketplace/personalization/rpc/PersonalizationRpc.dfc.new';
import { useAppliedFilters } from '../../filters/hooks/useAppliedFilters';
import { useAppContextQuery } from '../../hooks/useAppContextQuery';
import { useGates } from '../../hooks/useGates';
import { usePersonalizationQuery } from '../../hooks/usePersonalizationQuery';
import { useProductTypes } from '../../hooks/useProductTypeSearchParams';
import { SORT_OPTIONS } from '../components/SortDropdown';
import { DATA_TYPE_MAP, mapAppliedFiltersForRpc, mapSortForRpc, parseAppliedFilterValues } from '../utils/searchMappers';
import { useCollectionQuery } from './useCollectionQuery';
export const LISTINGS_PER_PAGE = 60;
const SEARCH_TIMEOUT_MS = 10000;
export function useSearchResultsQuery() {
  var _collectionResult$dat, _collectionResult$dat2, _searchResult$data;
  const gates = useGates();
  const {
    collectionSlug: urlCollectionSlug
  } = useParams();
  const collectionResult = useCollectionQuery();
  const collectionId = collectionResult.isUmbrellaCollection ? undefined : (_collectionResult$dat = collectionResult.data) === null || _collectionResult$dat === void 0 ? void 0 : _collectionResult$dat.id;
  const collectionSlug = collectionResult.isUmbrellaCollection ? undefined : (_collectionResult$dat2 = collectionResult.data) === null || _collectionResult$dat2 === void 0 ? void 0 : _collectionResult$dat2.slug;

  // Umbrella collections have no DS collection of their own and stay on the
  // legacy source, so keep their listing grid off the DS getCollectionBySlug
  // path. Only take the DS path once the collection lookup has resolved cleanly
  // — while it's loading, `isUmbrellaCollection` is still false, so gating on it
  // alone would transiently fire getCollectionBySlug for an umbrella slug.
  const useDSCollectionListings = gates.newDashboard && !!urlCollectionSlug && !collectionResult.loading && !collectionResult.error && !collectionResult.isUmbrellaCollection;
  const offset = useOffset();
  const sorts = useSorts();
  const searchResult = usePersonalizationQuery({
    app: SEARCH_QUERY,
    public: PUBLIC_SEARCH_QUERY,
    options: {
      context: {
        timeout: SEARCH_TIMEOUT_MS
      },
      skip: collectionResult.loading || !!collectionResult.error || useDSCollectionListings,
      variables: {
        collectionId,
        filter: useFilter(),
        length: LISTINGS_PER_PAGE,
        offset,
        queryType: useQueryType(),
        search: useSearchParams().get('search'),
        sorts
      }
    }
  });
  const dsFilter = useDSCollectionFilter();
  const dsCollectionResult = useAppContextQuery({
    app: GET_COLLECTION_BY_SLUG_QUERY,
    public: PUBLIC_GET_COLLECTION_BY_SLUG_QUERY,
    options: {
      context: {
        timeout: SEARCH_TIMEOUT_MS
      },
      skip: !useDSCollectionListings,
      variables: {
        slug: urlCollectionSlug !== null && urlCollectionSlug !== void 0 ? urlCollectionSlug : '',
        offset,
        limit: LISTINGS_PER_PAGE,
        filter: dsFilter,
        sorts: sorts !== null && sorts !== void 0 ? sorts : []
      }
    }
  });
  if (useDSCollectionListings) {
    var _dsCollectionResult$d;
    const dsData = (_dsCollectionResult$d = dsCollectionResult.data) === null || _dsCollectionResult$d === void 0 ? void 0 : _dsCollectionResult$d.result;
    return {
      loading: dsCollectionResult.loading,
      error: dsCollectionResult.error,
      data: dsData ? {
        result: {
          total: dsData.total,
          cards: dsData.items.map(adaptDSListingCard),
          // Collections have no AI answer; the grid reads these to pick its layout.
          aiAnswer: undefined,
          aiRequestId: undefined,
          queryType: undefined
        }
      } : undefined,
      queryType: undefined,
      collectionId: dsData === null || dsData === void 0 ? void 0 : dsData.entityId,
      collectionSlug: urlCollectionSlug
    };
  }
  return {
    loading: collectionResult.loading || searchResult.loading,
    error: collectionResult.error || searchResult.error,
    data: searchResult.data,
    queryType: (_searchResult$data = searchResult.data) !== null && _searchResult$data !== void 0 && _searchResult$data.result.queryType ? String(searchResult.data.result.queryType) : undefined,
    collectionId,
    collectionSlug
  };
}

// The DS and personalization ListingCard are the same mkpl-search model under
// different namespaces; default the personalization-only fields the grid reads.
function adaptDSListingCard(card) {
  var _card$listingName, _card$slug;
  return Object.assign({}, card, {
    listingName: (_card$listingName = card.listingName) !== null && _card$listingName !== void 0 ? _card$listingName : '',
    slug: (_card$slug = card.slug) !== null && _card$slug !== void 0 ? _card$slug : '',
    highlights: [],
    recommendationSources: [],
    listingTrellisIcon: undefined
  });
}
function useFilter() {
  const productTypes = useProductTypes();
  const appliedFilters = useAppliedFilters();
  const filtersByField = {};
  for (const filter of appliedFilters) {
    if (filter.field === 'PRODUCT_TYPE') {
      continue;
    }
    filtersByField[filter.field] = [{
      values: parseAppliedFilterValues(filter),
      clause: 'OR',
      negation: false,
      __typename: DATA_TYPE_MAP[filter.dataType]
    }];
  }
  filtersByField.PRODUCT_TYPE = [{
    values: productTypes,
    clause: 'OR',
    negation: false,
    __typename: DATA_TYPE_MAP.STRING
  }];
  return {
    filterGroups: [{
      filtersByField,
      clause: 'AND',
      negation: false
    }],
    clause: 'AND',
    negation: false
  };
}

// PRODUCT_TYPE is excluded: collection membership already scopes results and the
// product-type filter is hidden on collection pages. Undefined = search the whole collection.
function useDSCollectionFilter() {
  const appliedFilters = useAppliedFilters();
  const facetFilters = appliedFilters.filter(filter => filter.field !== 'PRODUCT_TYPE');
  if (facetFilters.length === 0) return undefined;
  return mapAppliedFiltersForRpc(facetFilters);
}
function useOffset() {
  const params = useSearchParams();
  const page = Number(params.get('page')) || 1;
  return (page - 1) * LISTINGS_PER_PAGE;
}
function useQueryType() {
  const params = useSearchParams();
  return params.get('queryType', {
    allowedValues: ['ELASTIC_SEARCH', 'VAAS', 'LLM']
  });
}
function useSorts() {
  const params = useSearchParams();
  const sort = params.get('sort', {
    allowedValues: Object.keys(SORT_OPTIONS)
  });
  return mapSortForRpc(sort);
}